import { useFetch } from "hooks/api";
import moment from "moment";
import { useMemo } from "react";
import { useAppSelector } from "redux/hooks";

export const groupByProfileName = (data) => {
  if (!data) {
    return [];
  }

  return (
    Object.values(
      data.reduce((acc, row) => {
        const name = row.accountInfo?.name;
        const type = row.accountInfo?.type;
        const key = `${name}-${type}`;

        if (acc[key]) {
          const prevRow = { ...acc[key] };

          if (prevRow.accountInfo.validPaymentMethod === false && row.accountInfo.validPaymentMethod) {
            prevRow.accountInfo.validPaymentMethod = row.accountInfo.validPaymentMethod;
          }
          const updatedRow = {
            [key]: {
              ...prevRow,
              marketPlaces: [...acc[key].marketPlaces, row.countryCode],
            },
          };
          return {
            ...acc,
            ...updatedRow,
          };
        }

        return {
          ...acc,
          [key]: { ...row, marketPlaces: [row.countryCode] },
        };
      }, {}),
    ) ?? []
  );
};

export const formatRawProfiles = (rawProfiles) => {
  return rawProfiles?.map((profile) => {
    return {
      profile_id: profile.profileId,
      entity_id: profile.accountInfo.id,
      profile_name: profile.accountInfo.name,
      profile_type: profile.accountInfo.type,
      is_kdp: profile.accountInfo.subType && profile.accountInfo.subType === "KDP_AUTHOR",
      marketplaces: profile.marketPlaces,
      valid_payment_method: profile.accountInfo.validPaymentMethod,
    };
  });
};

export const useAmazonProfiles = (filterDuplicates = false, fetchRemoteProfiles = true) => {
  const user = useAppSelector((state) => state.user);
  const channel = useAppSelector((state) => state.channel);

  const { data: trellisProfilesData } = useFetch(
    [channel, user.organization_id, "trellisProfiles"],
    "/api/organizations/",
    { page_size: 5000 },
    {
      select: (d) => d.data?.filter((profile) => profile.org_type === "AMAZON"),
      default: [],
    },
  );

  const { data: remoteProfilesData, isLoading: remoteProfilesLoading } = useFetch(
    [filterDuplicates ? "availableRemoteProfiles" : "remoteProfiles", channel, user.organization_id],
    "/api/organizations/",
    {
      page_size: 5000,
      remote_profiles: true,
      filter_out_duplicates: filterDuplicates,
    },
    {
      select: (d) => d.data,
      enabled: fetchRemoteProfiles,
      default: [],
    },
  );

  const { data: allAccountManagerProfilesData, isLoading: isAllAccountManagerProfilesDataLoading } = useFetch(
    ["allAccountManagerProfiles"],
    "/advertising/allprofiles",
    {
      org_id: parseInt(user.organization_id),
    },
    { enabled: !!user.organization_id, select: (res) => res.data },
  );

  const formattedRemoteProfilesData = useMemo(() => {
    const rawProfiles = groupByProfileName(remoteProfilesData) ?? [];
    const formattedProfiles = formatRawProfiles(rawProfiles);

    return formattedProfiles;
  }, [remoteProfilesData]);

  const availableRemoteProfilesData = useMemo(() => {
    return formattedRemoteProfilesData?.filter((remoteProfile) => {
      const profileAvailable =
        ["seller", "vendor"].includes(remoteProfile.profile_type) &&
        !trellisProfilesData?.some(
          (trellisProfile) =>
            trellisProfile.profile_name === remoteProfile.profile_name &&
            trellisProfile.profile_type === remoteProfile.profile_type,
        ) &&
        remoteProfile.valid_payment_method &&
        !remoteProfile.is_kdp;

      return profileAvailable;
    });
  }, [formattedRemoteProfilesData, trellisProfilesData]);

  const groupedProfilesByClient = useMemo(() => {
    const formattedAccountManagerClientData = [];

    allAccountManagerProfilesData?.map((adsClient) => {
      const { created_on, amazon_user_id, amazon_user_email, amazon_user_name } = adsClient;
      const id = adsClient.client_id;
      const rawProfiles = groupByProfileName(adsClient.profiles) ?? [];
      const formattedProfiles = formatRawProfiles(rawProfiles);

      const formattedAvailableProfiles = formattedProfiles?.filter((remoteProfile) => {
        const profileAvailable =
          // Filter out profile if it's available but already showing in previous AmazonAdsClient profile list
          !formattedAccountManagerClientData
            ?.map((client) => client.profiles?.map((profile) => profile.profile_id))
            ?.flat()
            ?.includes(remoteProfile?.profile_id);

        return profileAvailable;
      });

      const label = `Added on ${moment(created_on.slice(0, 10)).format("MMMM DD, YYYY")}`;
      formattedAccountManagerClientData.push({
        id,
        profiles: formattedAvailableProfiles,
        label,
        amazon_user_id,
        amazon_user_email,
        amazon_user_name,
      });
    });

    return formattedAccountManagerClientData;
  }, [allAccountManagerProfilesData]);

  const groupedAvailableProfilesByClient = useMemo(() => {
    const filteredClientProfiles = groupedProfilesByClient?.map((client) => {
      const availableProfiles = client.profiles.filter(
        (remoteProfile) =>
          ["seller", "vendor"].includes(remoteProfile.profile_type) &&
          !trellisProfilesData?.some(
            (trellisProfile) =>
              trellisProfile.profile_name === remoteProfile.profile_name &&
              trellisProfile.profile_type === remoteProfile.profile_type,
          ),
      );
      return { ...client, profiles: availableProfiles };
    });

    return filteredClientProfiles;
  }, [groupedProfilesByClient, trellisProfilesData]);

  return {
    trellisProfilesData,
    formattedRemoteProfilesData,
    availableRemoteProfilesData,
    remoteProfilesData,
    groupedProfilesByClient,
    groupedAvailableProfilesByClient,
    isLoading: remoteProfilesLoading || isAllAccountManagerProfilesDataLoading,
  };
};
